import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../api/api";
import "./GeneralSettings.css";
import {
  User,
  Lock,
  Camera,
  Trash2,
  Bell,
  Check,
  Share2,
  Shield,
  ChevronRight,
  LogOut,
  Users,
  Unlink,
} from "lucide-react";

import { useFormAction } from "../context/FormActionContext";
import Loader from "./Loader";

const GeneralSettings = () => {
  const [loading, setLoading] = useState(true);
  const { registerSaveHandler } = useFormAction();
  const [error, setError] = useState("");

  // User Profile State
  const [profile, setProfile] = useState({
    name: "",
    username: "",
    email: "",
    phone: "",
    role: "",
    bio: "",
    shopName: "",
    photoUrl: null,
    slack_channel_id: null,
    slack_team_name: null,
  });

  // Password State
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Notifications State
  const [notifications, setNotifications] = useState({
    orders: false,
    affiliate: false,
    inventory: false,
  });

  // Brand State
  const [brand, setBrand] = useState(null);
  const [shopsCount, setShopsCount] = useState(0);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [isSlackConnecting, setIsSlackConnecting] = useState(false);

  const fileInputRef = useRef(null);
  const slackPollingRef = useRef(null);
  const navigate = useNavigate();

  // Stop polling when Slack is connected or component unmounts
  useEffect(() => {
    if (profile.slack_channel_id && slackPollingRef.current) {
      clearInterval(slackPollingRef.current);
      slackPollingRef.current = null;
      setIsSlackConnecting(false);
    }
  }, [profile.slack_channel_id]);

  useEffect(() => {
    return () => {
      if (slackPollingRef.current) {
        clearInterval(slackPollingRef.current);
      }
    };
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    // Creating a preview URL immediately
    const previewUrl = URL.createObjectURL(file);
    setProfile((prev) => ({ ...prev, photoUrl: previewUrl }));

    // TODO: Direct upload or wait for save?
    // Usually photos are uploaded immediately.
    // I'll assume we need to upload it now.
    const formData = new FormData();
    formData.append("file", file);

    try {
      const resp = await apiClient.post("/user/upload-photo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      if (resp.data?.url) {
        setProfile((prev) => ({ ...prev, photoUrl: resp.data.url }));
      }
    } catch (err) {
      console.error("Upload failed", err);
      // alert("Failed to upload photo");
      // Keep preview or revert? For now keep preview as optimistic.
    }
  };

  const handleRemovePhoto = async () => {
    // Optimistic UI update
    setProfile((prev) => ({ ...prev, photoUrl: null }));

    try {
      // Immediate persistence for removal to match upload behavior
      await apiClient.put(
        "/user/profile",
        { profile_photo_url: null },
        {
          withCredentials: true,
        }
      );
    } catch (err) {
      console.error("Failed to remove photo persistently", err);
      // Optionally alert the user, but optimistic UI usually suffices unless they reload immediately
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      // Now simply call /user/profile - the backend handles the cookie
      const resp = await apiClient.get("/user/profile", {
        credentials: "include",
        withCredentials: true,
      });

      console.log("DEBUG: fetchProfile response:", resp.data); // Debug log

      if (resp.data?.user) {
        const detailedUser = resp.data.user;
        setProfile({
          name: detailedUser.name || "",
          username: detailedUser.username || "",
          email: detailedUser.email || "",
          phone: detailedUser.phone_number || "",
          // If role is 'user' (legacy/invalid), default to 'brand_admin' so we can save successfully
          role:
            detailedUser.role === "admin" || !detailedUser.role
              ? "brand_admin"
              : detailedUser.role,
          bio: detailedUser.bio || "",
          shopName: detailedUser.shop_name || "",
          photoUrl: detailedUser.profile_photo_url || null,
          slack_channel_id: detailedUser.slack_channel_id || null,
          slack_team_name: detailedUser.slack_team_name || null,
        });
        setNotifications({
          orders: detailedUser.notify_order_updates || false,
          affiliate: detailedUser.notify_affiliate_activity || false,
          inventory: detailedUser.notify_inventory_alerts || false,
        });
        setBrand(resp.data.brand);
        setShopsCount(resp.data.shopsCount || 0);
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError("Failed to load settings.");
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleToggle = (key) => {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
  };

  const handleSave = React.useCallback(async () => {
    // Collect data
    const payload = {
      name: profile.name,
      username: profile.username,
      email: profile.email,
      phone_number: profile.phone,
      role: profile.role,
      bio: profile.bio,
      notify_order_updates: notifications.orders,
      notify_affiliate_activity: notifications.affiliate,
      notify_inventory_alerts: notifications.inventory,
      // profile_photo_url: profile.photoUrl, // Don't send photo URL, handled by upload/remove handlers directly
    };

    if (passwordForm.newPassword) {
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        alert("Passwords do not match!");
        return;
      }
      payload.current_password = passwordForm.currentPassword;
      payload.new_password = passwordForm.newPassword;
    }

    try {
      console.log("GeneralSettings: handleSave executing...");
      await apiClient.put("/user/profile", payload, {
        withCredentials: true,
        credentials: "include",
      });
      alert("Settings saved successfully!");
      // Clear password fields
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      console.error(err);
      alert(
        "Failed to save settings: " +
          (err.response?.data?.detail || err.message)
      );
    }
  }, [profile, notifications, passwordForm]);

  const handleDeleteAccount = async () => {
    try {
      await apiClient.delete("/user/delete", {
        withCredentials: true,
        credentials: "include",
      });
      // Redirect to home/login on success
      window.location.href = "/";
    } catch (err) {
      console.error("Delete account failed", err);
      alert("Failed to delete account. Please try again.");
      setShowDeleteConfirmDialog(false);
    }
  };

  const handleConnectSlack = async () => {
    try {
      const resp = await apiClient.get("/slack/slack-oauth-url", {
        withCredentials: true,
        credentials: "include",
      });
      if (resp.data?.oauth_url) {
        // Center the popup
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const popup = window.open(
          resp.data.oauth_url,
          "SlackConnection",
          `width=${width},height=${height},left=${left},top=${top},status=no,location=no,toolbar=no,menubar=no`
        );

        if (popup) {
          setIsSlackConnecting(true);
          
          // Start Polling
          if (slackPollingRef.current) clearInterval(slackPollingRef.current);
          
          let attempts = 0;
          slackPollingRef.current = setInterval(() => {
            attempts++;
            if (attempts > 100) { // Timeout after ~5 minutes
                clearInterval(slackPollingRef.current);
                slackPollingRef.current = null;
                setIsSlackConnecting(false);
                return;
            }
            if (popup.closed) {
                 // If user closes manually, we still do one check then stop if not connected?
                 // User request: "when we find... in DB it will automatically update"
                 // If popup is closed, they might have finished or cancelled. 
                 // Let's keep polling for a few more seconds then stop if popup closed?
                 // Or just let it run until connected or timeout? 
                 // If popup is closed and NOT connected, user likely cancelled.
                 // let's stop polling if popup is closed AND we haven't detected connection yet (via effect).
                 // Actually, checking popup.closed is good hygiene.
                 // If it is closed, give it a few more grace polls? 
                 // Simpler: Just poll. If popup closed, we can stop polling after a short delay or immediately.
                 // The user said: "automatic update".
                 // Let's polling continuously for now.
            }
            fetchProfile();
          }, 3000);
        }
      }
    } catch (err) {
      console.error("Failed to initiate Slack connection", err);
      alert("Failed to initiate Slack connection. Please try again.");
    }
  };

  const handleDisconnectSlack = async () => {
    if (!window.confirm("Are you sure you want to disconnect Slack? This will stop all alerts and reports.")) {
      return;
    }

    try {
      const resp = await apiClient.post("/slack/disconnect", {}, {
        withCredentials: true,
        credentials: "include",
      });
      
      if (resp.data?.status === "success") {
        setProfile((prev) => ({
          ...prev,
          slack_channel_id: null,
          slack_team_name: null,
        }));
        alert("Slack disconnected successfully.");
      }
    } catch (err) {
      console.error("Failed to disconnect Slack", err);
      alert("Failed to disconnect Slack. Please try again.");
    }
  };

  // Register the save handler for the global header button
  useEffect(() => {
    console.log("GeneralSettings: Registering handleSave");
    // Only register if handleSave is defined
    const unregister = registerSaveHandler(handleSave);
    return () => {
      console.log("GeneralSettings: Unregistering handleSave");
      unregister();
    };
  }, [registerSaveHandler, handleSave]);



  return (
    <div className="page-relative-wrapper">
      <div className="settings-container">
        {/* Profile Information */}
        <div
          className={`settings-section-card ${
            loading ? "shimmer skeleton" : ""
          }`}
        >
          <div className="section-header">
            <div className="icon-box">
              <User size={22} color="#ec1c1cff" fill="rgba(255,255,255,0.1)" />
            </div>
            <div className="section-title">
              <h3>Profile Information</h3>
              <p>Update your personal details and photo</p>
            </div>
          </div>

          <div className="profile-photo-section">
            <label className="profile-photo-label">Profile Photo</label>
            <div className="photo-actions">
              {profile.photoUrl ? (
                <img
                  src={profile.photoUrl}
                  alt="Profile"
                  className="current-photo"
                />
              ) : (
                <div className="no-photo">
                  {profile.name ? profile.name[0] : "AC"}
                </div>
              )}
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <div
                  style={{ display: "flex", gap: "12px", alignItems: "center" }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept="image/png, image/jpeg, image/gif"
                    onChange={handleFileChange}
                  />
                  <button
                    className="upload-btn"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <Camera size={16} /> Upload New Photo
                  </button>
                  <button className="remove-btn" onClick={handleRemovePhoto}>
                    <Trash2 size={16} /> Remove
                  </button>
                </div>
                <span className="photo-hint" style={{ marginLeft: 0 }}>
                  JPG, PNG or GIF. Max size 5MB.
                </span>
              </div>
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                className="form-input"
                name="name"
                value={profile.name}
                onChange={handleProfileChange}
                placeholder="e.g. Alex Chen"
              />
            </div>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                className="form-input"
                name="username"
                value={profile.username}
                onChange={handleProfileChange}
                placeholder="e.g. alexchen"
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                className="form-input"
                name="email"
                value={profile.email}
                onChange={handleProfileChange}
                placeholder="e.g. alex@example.com"
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                className="form-input"
                name="phone"
                value={profile.phone}
                onChange={handleProfileChange}
                placeholder="+1 (555) 000-0000"
                autoComplete="off"
              />
            </div>
            <div className="form-group full-width">
              <label>Role</label>
              <select
                className="form-select"
                name="role"
                value={profile.role}
                onChange={handleProfileChange}
              >
                <option value="brand_admin">Brand Admin</option>
                <option value="shop_manager">Shop Manager</option>
              </select>
            </div>
            <div className="form-group full-width">
              <label>Bio</label>
              <textarea
                className="form-textarea"
                name="bio"
                value={profile.bio}
                onChange={handleProfileChange}
                placeholder="Tell us about yourself..."
                style={{ minHeight: "120px" }}
                autoComplete="off"
              ></textarea>
            </div>
          </div>
          <div style={{ textAlign: "right", marginTop: "24px" }}>
            <button
              type="button"
              className="upload-btn"
              onClick={handleSave}
              style={{
                display: "inline-flex",
                background:
                  "linear-gradient(90deg, rgba(16, 122, 64, 0.5) 20%, rgba(2, 106, 40, 0.1) 90%)",
                border: "none",
                padding: "12px 24px",
                borderRadius: "14px",
              }}
            >
              Save Profile Info
            </button>
          </div>
        </div>

        {/* Change Password */}
        <div
          className={`settings-section-card ${
            loading ? "shimmer skeleton" : ""
          }`}
        >
          <div className="section-header">
            <div className="icon-box green">
              <Shield
                size={22}
                color="#10b981"
                fill="rgba(16, 185, 129, 0.1)"
              />
            </div>
            <div className="section-title">
              <h3>Change Password</h3>
              <p>Manage your password</p>
            </div>
          </div>
          <div className="form-group full-width" style={{ marginBottom: 20 }}>
            <label>Current Password</label>
            <input
              type="password"
              className="form-input"
              name="currentPassword"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
              placeholder="Enter current password"
              autoComplete="new-password"
            />
          </div>
          <div className="form-group full-width" style={{ marginBottom: 20 }}>
            <label>New Password</label>
            <input
              type="password"
              className="form-input"
              name="newPassword"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              placeholder="Enter new password"
              autoComplete="new-password"
            />
          </div>
          <div className="form-group full-width" style={{ marginBottom: 8 }}>
            <label>Confirm New Password</label>
            <input
              type="password"
              className="form-input"
              name="confirmPassword"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              placeholder="Confirm new password"
              autoComplete="new-password"
            />
          </div>
          <button className="primary-btn" onClick={handleSave}>
            Update Password
          </button>
        </div>

        {/* Email Notifications */}
        <div
          className={`settings-section-card ${
            loading ? "shimmer skeleton" : ""
          }`}
        >
          <div className="section-header">
            <div className="icon-box">
              <Bell size={25} color="#10b981" fill="rgba(16, 185, 129, 0.1)" />
            </div>
            <div className="section-title">
              <h3>Email Notifications</h3>
              <p>Choose what updates you want to receive</p>
            </div>
          </div>

          <div className="toggle-row">
            <div className="toggle-info">
              <h4>Order Updates</h4>
              <p>Get notified when orders are placed or updated</p>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={notifications.orders}
                onChange={() => handleToggle("orders")}
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="toggle-row">
            <div className="toggle-info">
              <h4>Affiliate Activity</h4>
              <p>Updates on affiliate posts and performance</p>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={notifications.affiliate}
                onChange={() => handleToggle("affiliate")}
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="toggle-row">
            <div className="toggle-info">
              <h4>Inventory Alerts</h4>
              <p>Low stock and out of stock notifications</p>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={notifications.inventory}
                onChange={() => handleToggle("inventory")}
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>

        {/* Linked Accounts */}
        <div
          className={`settings-section-card ${
            loading ? "shimmer skeleton" : ""
          }`}
        >
          <div className="section-header">
            <div className="icon-box green">
              <Share2
                size={25}
                color="#10b981"
                fill="rgba(16, 185, 129, 0.1)"
              />
            </div>
            <div className="section-title">
              <h3>Linked Accounts</h3>
              <p>Connect your social media and platform accounts</p>
            </div>
          </div>

          {/* TikTok Shop */}
          <div className="linked-account-row">
            <div className="account-info">
              <div className="platform-icon tiktok-bg">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="#fff"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.17-2.86-.6-4.12-1.31-.01 2.41.02 4.81-.01 7.22-.03 3.41-2.03 6.6-5.41 7.74-3.37 1.18-7.5-.15-9.35-3.31-1.63-2.67-1.42-6.52.81-8.91 1.71-1.89 4.41-2.58 6.78-1.85v4.11c-1.12-.53-2.54-.45-3.56.23-.97.66-1.55 1.83-1.46 3.01.12 1.6 1.57 2.87 3.16 2.76 1.74-.01 3.08-1.57 3.02-3.3-.01-4.73-.01-9.45-.01-14.18.01-.15.01-.31.02-.46-.01-.34-.01-.67-.01-1.01z" />
                </svg>
              </div>
              <div className="platform-details">
                <h4>Shop</h4>
                <p>{shopsCount > 0 ? `Connected` : "Not connected"}</p>
              </div>
            </div>
            <div className="connection-status">
              {shopsCount > 0 ? (
                <>
                  <span className="status-badge status-connected">
                    Connected
                  </span>
                </>
              ) : (
                <button
                  className="action-btn"
                  onClick={() => navigate("/myshops")}
                >
                  Connect
                </button>
              )}
            </div>
          </div>

          {/* TikTok Ads */}
          <div className="linked-account-row">
            <div className="account-info">
              <div className="platform-icon tiktok-ads-bg">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="#fff"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.17-2.86-.6-4.12-1.31-.01 2.41.02 4.81-.01 7.22-.03 3.41-2.03 6.6-5.41 7.74-3.37 1.18-7.5-.15-9.35-3.31-1.63-2.67-1.42-6.52.81-8.91 1.71-1.89 4.41-2.58 6.78-1.85v4.11c-1.12-.53-2.54-.45-3.56.23-.97.66-1.55 1.83-1.46 3.01.12 1.6 1.57 2.87 3.16 2.76 1.74-.01 3.08-1.57 3.02-3.3-.01-4.73-.01-9.45-.01-14.18.01-.15.01-.31.02-.46-.01-.34-.01-.67-.01-1.01z" />
                </svg>
              </div>
              <div className="platform-details">
                <h4>Ads</h4>
                <p>Not connected</p>
              </div>
            </div>
            <div className="connection-status">
              <button className="action-btn">Connect</button>
            </div>
          </div>

          {/* Slack */}
          <div className="linked-account-row">
            <div className="account-info">
              <div className="platform-icon slack-bg">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="#fff"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.263 0a2.527 2.527 0 0 1 2.522-2.52 2.527 2.527 0 0 1 2.522 2.52v6.313A2.528 2.528 0 0 1 8.827 24a2.528 2.528 0 0 1-2.522-2.522v-6.313zM8.827 5.042a2.528 2.528 0 0 1-2.522-2.52A2.528 2.528 0 0 1 8.827 0a2.527 2.527 0 0 1 2.522 2.522v2.52H8.827zm0 1.263a2.527 2.527 0 0 1 2.522 2.522 2.527 2.527 0 0 1-2.522 2.522H2.514A2.528 2.528 0 0 1 0 8.827a2.528 2.528 0 0 1 2.514-2.522h6.313zm10.131 3.86a2.528 2.528 0 0 1 2.522-2.522A2.528 2.528 0 0 1 24 10.165a2.527 2.527 0 0 1-2.522 2.52h-2.52v-2.52zm-1.263 0a2.527 2.527 0 0 1-2.522 2.52 2.527 2.527 0 0 1-2.522-2.52V3.852A2.528 2.528 0 0 1 15.173 1.33a2.528 2.528 0 0 1 2.522 2.522v6.313zM15.173 18.958a2.528 2.528 0 0 1 2.522 2.522A2.528 2.528 0 0 1 15.173 24a2.527 2.527 0 0 1-2.522-2.52v-2.522h2.522zm0-1.263a2.527 2.527 0 0 1-2.522-2.522 2.527 2.527 0 0 1 2.522-2.522h6.313A2.528 2.528 0 0 1 24 15.173a2.528 2.528 0 0 1-2.514 2.522h-6.313z" />
                </svg>
              </div>
              <div className="platform-details">
                <h4>Slack</h4>
                <p>
                  {profile.slack_channel_id
                    ? `Connected (${profile.slack_team_name})`
                    : "Not connected"}
                </p>
              </div>
            </div>
            <div className="connection-status">
              {profile.slack_channel_id ? (
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <span className="status-badge status-connected">Connected</span>
                  <button 
                    className="remove-btn" 
                    onClick={handleDisconnectSlack}
                    style={{ padding: "8px", fontSize: "12px" }}
                    title="Disconnect Slack"
                  >
                    <Unlink size={14} />
                  </button>
                </div>
              ) : (
                <button className="action-btn" onClick={handleConnectSlack}>
                  Connect
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Privacy Settings */}
        <div
          className={`settings-section-card ${
            loading ? "shimmer skeleton" : ""
          }`}
        >
          <div className="section-header">
            <div className="icon-box">
              <Lock size={25} color="#10b981" fill="rgba(16, 185, 129, 0.1)" />
            </div>
            <div className="section-title">
              <h3>Privacy Settings</h3>
              <p>Control your data and visibility</p>
            </div>
          </div>

          <button 
            className="delete-account-btn"
            onClick={() => setShowDeleteConfirmDialog(true)}
          >
            <div className="delete-content">
              <div
                style={{ background: "#450a0a", padding: 8, borderRadius: 6 }}
              >
                <Trash2 size={16} color="#fca5a5" />
              </div>
              <div className="delete-text">
                <h4>Delete Account</h4>
                <p>Permanently delete your account</p>
              </div>
            </div>
            <ChevronRight size={20} color="#52525b" />
          </button>
        </div>

        {/* Sign Out Button */}
        <div className="sign-out-container">
          <button
            className="sign-out-link"
            onClick={async () => {
              try {
                await apiClient.post("/logout");
                window.location.href = "/";
              } catch (e) {
                console.error("Logout failed", e);
                window.location.href = "/";
              }
            }}
          >
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmDialog && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Delete Account?</h3>
              <p>
                This action cannot be undone. This will permanently delete your account, 
                connected shops, and all associated metric data.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={() => setShowDeleteConfirmDialog(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-delete-confirm"
                onClick={handleDeleteAccount}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Slack Connection Blur Overlay */}
      {isSlackConnecting && (
        <div 
          className="modal-overlay" 
          style={{ cursor: 'pointer' }}
          onClick={() => setIsSlackConnecting(false)}
        >
          <div className="modal-content" style={{ textAlign: 'center', background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(12px)' }}>
            <div className="section-header" style={{ justifyContent: 'center', border: 'none', marginBottom: 0 }}>
              <div className="icon-box" style={{ background: 'rgba(74, 21, 75, 0.1)' }}>
                <Share2 size={32} color="#4A154B" />
              </div>
            </div>
            <h3 style={{ margin: '20px 0 10px', color: '#fff' }}>Connecting to Slack...</h3>
            <p style={{ color: '#a1a1aa', fontSize: '0.9rem' }}>Please complete the authorization in the popup window.</p>
            <button 
              className="btn-cancel" 
              style={{ marginTop: '24px', width: '100%' }}
              onClick={() => setIsSlackConnecting(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper for cookie
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

export default GeneralSettings;
